import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../../_services/auth.service';
import { PaymentService } from '../../../_services/subscription/payment.service';
import { LoaderService } from '../../../_services/loader.service';
import { ToastService } from '../../../_services/toast.service';
import { RouterModule } from '@angular/router';
import {
  PLANS,
  PaymentPlan,
} from '../../payment-plan/payment-plan.component';

const DAY_MS = 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  profileForm!: FormGroup;
  userDetails: any = null;
  isBrowser = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private _SPS: PaymentService,
    private loader: LoaderService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private _NS: ToastService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.profileForm = this.fb.group({
      name: [''],
      email: [''],
      phone: [{ value: '', disabled: true }],
      countryCode: ['+91'],
    });
  }

  ngOnInit(): void {
    // When user logs in while app is running
    this.authService.isLoggedIn$().subscribe((loggedIn) => {
      if (loggedIn) {
        this.loadUserIntoForm();
      }
    });

    // When user refreshes page on profile
    if (this.authService.isLoggedIn()) {
      this.loadUserIntoForm();
      this.getUser();
      this.getTransaction();
    }
  }

  /** Plan details for the user's membership type (e.g. "Intelligence"), if it is a known plan. */
  get membershipPlan(): PaymentPlan | null {
    const type = String(this.userDetails?.subsctions?.membershipType ?? '')
      .trim()
      .toLowerCase();
    if (!type) return null;
    return (
      PLANS.find(
        (plan) => plan.id === type || plan.name.toLowerCase() === type,
      ) ?? null
    );
  }

  get isSubscriptionActive(): boolean {
    return this.userDetails?.subsctions?.subscriptionStatus === 'active';
  }

  get daysLeft(): number | null {
    const end = Date.parse(this.userDetails?.subsctions?.subscriptionEndDate);
    if (Number.isNaN(end)) return null;
    return Math.max(0, Math.ceil((end - Date.now()) / DAY_MS));
  }

  /** Share of the subscription period already used, 0-100. */
  get subscriptionProgress(): number {
    const start = Date.parse(
      this.userDetails?.subsctions?.subscriptionStartDate,
    );
    const end = Date.parse(this.userDetails?.subsctions?.subscriptionEndDate);
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
    const used = ((Date.now() - start) / (end - start)) * 100;
    return Math.min(100, Math.max(0, Math.round(used)));
  }

  get initials(): string {
    const parts = String(this.userDetails?.name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return '?';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (parts[0][0] + last).toUpperCase();
  }

  get viewedCount(): number {
    return this.userDetails?.viewedProperties?.length ?? 0;
  }

  get viewLimit(): number {
    return this.userDetails?.viewLimit ?? 3;
  }

  get viewsProgress(): number {
    if (!this.viewLimit) return 0;
    return Math.min(100, Math.round((this.viewedCount / this.viewLimit) * 100));
  }

  // 🔥 Autofill form from stored login response
  userPlan: string = '';

  loadUserIntoForm() {
    const stored = this.authService.getUser();
    if (!stored) return;

    const user = stored.data ? stored.data : stored;

    this.userDetails = user;
    this.userPlan = user.isUserType || '';

    console.log('Plan Type:', this.userPlan);

    this.profileForm.patchValue({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      countryCode: '+91',
    });
  }

  // Save button click
  saveProfile() {
    if (this.profileForm.invalid) return;

    const stored = this.authService.getUser();
    if (!stored) return;

    const updatedFormValues = this.profileForm.value;

    // Preserve original API response structure
    stored.data = {
      ...stored.data,
      ...updatedFormValues,
    };
    this.editUserDetails();
    localStorage.setItem('Cirlced_User', JSON.stringify(stored));
  }

  getUser() {
    this.authService.getUserDetails().subscribe((res: any) => {
      this.userDetails = res;
      console.log('res', res);
    });
  }
  getCurrentOrder: any;
  getTransaction() {
    this._SPS.getTransaction().subscribe({
      next: (res: any) => {
        if (res?.length) {
          const lastTransactionId =
            this.userDetails?.subsctions?.lastTransactionId;

          this.getCurrentOrder = res.find(
            (item: any) => item.transactions?.order_id === lastTransactionId,
          );
          console.log('res ffff', this.getCurrentOrder);
        }
      },
      error: (err) => {
        console.error('Download failed', err);
      },
    });
  }
  downloadReceipt() {
    this.loader.show();
    this._SPS.downloadReceipt(this.getCurrentOrder.invoiceNo).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${this.getCurrentOrder.transactions.orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        this.loader.hide();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Download failed', err);
      },
    });
  }
  editUserDetails() {
    if (this.profileForm.invalid) {
      this._NS.error('Fill user details');
      return;
    }
    this.loader.show();
    this._SPS.editUser(this.userDetails._id, this.profileForm.value).subscribe({
      next: (res: any) => {
        if (res.status) {
          this._NS.success(res.message);
          this.loader.hide();
        }
      },
      error: (err) => {
        this.loader.hide();
        console.error('Edit User', err);
      },
    });
  }
}
